import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Users, Clock } from "lucide-react";
import { Event } from "@/hooks/useEvents";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

interface EventCardProps {
  event: Event;
}

const EVENT_TYPE_LABELS: Record<Event['event_type'], string> = {
  ride: 'Ride',
  garage_day: 'Garage Day',
  cleanup: 'Cleanup',
  meetup: 'Meetup',
  training: 'Training',
  overlanding_trip: 'Overlanding Trip',
  competition: 'Competition',
};

const EVENT_TYPE_COLORS: Record<Event['event_type'], string> = {
  ride: 'bg-primary text-primary-foreground',
  garage_day: 'bg-orange-500 text-white',
  cleanup: 'bg-green-500 text-white',
  meetup: 'bg-blue-500 text-white',
  training: 'bg-purple-500 text-white',
  overlanding_trip: 'bg-amber-600 text-white',
  competition: 'bg-red-500 text-white',
};

const EventCard = ({ event }: EventCardProps) => {
  const navigate = useNavigate();
  const coverPhoto = event.photo_urls?.[0] || event.photo_url;

  return (
    <Card className="hover:shadow-lg transition-shadow cursor-pointer overflow-hidden" onClick={() => navigate(`/events/${event.id}`)}>
      {coverPhoto && (
        <div className="aspect-video w-full overflow-hidden">
          <img 
            src={coverPhoto} 
            alt={event.title}
            className="w-full h-full object-cover photo-signature"
          />
        </div>
      )}
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-xl mb-2">{event.title}</CardTitle>
            <Badge className={EVENT_TYPE_COLORS[event.event_type]}>
              {EVENT_TYPE_LABELS[event.event_type]}
            </Badge>
            {event.status !== 'upcoming' && (
              <Badge variant="outline" className="ml-2">
                {event.status}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center text-sm text-muted-foreground">
          <Calendar className="w-4 h-4 mr-2" />
          {format(new Date(event.start_date), 'PPP p')}
        </div>

        {event.meeting_point_description && (
          <div className="flex items-center text-sm text-muted-foreground">
            <MapPin className="w-4 h-4 mr-2" />
            {event.meeting_point_description}
          </div>
        )}

        {event.estimated_duration_hours && (
          <div className="flex items-center text-sm text-muted-foreground">
            <Clock className="w-4 h-4 mr-2" />
            {event.estimated_duration_hours} hours
          </div>
        )}

        {event.max_participants && (
          <div className="flex items-center text-sm text-muted-foreground">
            <Users className="w-4 h-4 mr-2" />
            Max {event.max_participants} participants
          </div>
        )}

        {event.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {event.description}
          </p>
        )}

        <Button 
          className="w-full mt-4" 
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/events/${event.id}`);
          }}
        >
          View Details
        </Button>
      </CardContent>
    </Card>
  );
};

export default EventCard;
